import { PetQuote, PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import {
    CoverageSettings,
    Policy,
    Quote,
    PartialPolicySchema,
    QuoteSchema,
    Pet,
    PetSchema,
    DefaultPolicyAmounts,
    InitialPetValues,
    PartialBillingStepType,
    PartialQuoteSchema,
    PartialBillingStepSchema
} from "@/shared/types/Quote.interface";
import { PetPolicy } from "@spotpetinsurance/spot-types/entities/PetPolicy";
import { DateTime } from "luxon";
import { Coverage, CoverageType, CoverageTypeDifference } from "@spotpetinsurance/spot-types/entities/Coverage";
import { z } from "zod";
import { Species } from "@spotpetinsurance/spot-types/entities/Species";
import isNumber from "lodash/isNumber";
import { phone } from "phone";
import { PublicConfig } from "../PublicConfig";
import Strings from "./Strings.constants";
import { UIUtils } from "./UIUtils";
import { DefaultPlanResponse } from "./QuoteAPI";
import { DefaultPlanUtils } from "./DefaultPlanUtils";
import { StorageUtils } from "./StorageUtils";
import currency from "currency.js";
import { TMP_POLICY_PREFIX } from "./constants";

interface CreateNewPolicyUSParams {
    currentSettings?: CoverageSettings;
    isMultiPetLinked?: boolean;
    defaultPlan?: DefaultPlanResponse;
    underwriter?: PetUnderwriterType;
    planCoverageOverride?: CoverageSettings;
    /** ENG-16914: defaults to Dog. Only the cat personalization arm overrides this. */
    species?: Species;
}

export class QuoteDataUtils {
    static getUnderwriterFromFormId(formId: string | undefined): PetUnderwriterType {
        if (!formId) throw new Error(`formId is required`);
        if (formId.toLowerCase().includes("ptz-us")) {
            return "ptz-us";
        }
        if (formId.toLowerCase().includes("ptz-ca")) {
            return "ptz-ca";
        }
        throw new Error(`Unknown formId: ${formId}`);
    }

    static getBillingStepInitialValues(quote?: Quote): PartialBillingStepType {
        const defaultAddress = {
            street1: "",
            street2: "",
            city: "",
            state: "",
            zipCode: ""
        };

        // Destructure existing values from quote
        const { billingInfo } = quote || {};

        const firstName = quote?.firstName?.trim().replace(/\s+/g, " ") ?? "";
        const lastName = quote?.lastName?.trim().replace(/\s+/g, " ") ?? "";
        const isPetParent = firstName?.toLowerCase() === "pet" && lastName?.toLowerCase() === "parent";
        const hasFullName = !!firstName && !!lastName;
        const initialNameOnCard = isPetParent || !hasFullName ? "" : `${firstName} ${lastName}`;

        const initialValues: PartialBillingStepType = {
            firstName: isPetParent ? "" : firstName || "",
            lastName: isPetParent ? "" : lastName || "",
            billingInfo: {
                firstName: isPetParent ? "" : firstName || "",
                lastName: isPetParent ? "" : lastName || "",
                nameOnCard: !!billingInfo?.nameOnCard ? billingInfo.nameOnCard : UIUtils.truncateString(initialNameOnCard ?? "", 50, false),
                address: {
                    ...defaultAddress,
                    ...quote?.ratingAddress,
                    ...billingInfo?.address
                },
                frequency: billingInfo?.frequency || "monthly"
            },
            phone: quote?.phone || "",
            paperless: true,
            extra: {
                termsConsent: quote?.extra?.termsConsent || false
            }
        };

        const storedValues = StorageUtils.getItem(`previous-values-billing`);
        if (storedValues) {
            try {
                const parsedValues = JSON.parse(storedValues);
                const validatedValues = PartialBillingStepSchema.safeParse(parsedValues).data;
                if (validatedValues) {
                    const mergedValues = UIUtils.deepMerge(initialValues, validatedValues, PartialBillingStepSchema);
                    return mergedValues.mergedBase;
                }
            } catch (error) {
                console.error("Failed to parse previous billing values:", error);
            }
        }
        return initialValues;
    }

    static createNewPolicyCA(): InitialPetValues {
        return {
            name: ``,
            species: Species.Dog,
            gender: `F`,
            age: undefined,
            birthMonth: "",
            birthYear: "",
            breedID: ``
        };
    }

    static createNewPolicyUS(params: CreateNewPolicyUSParams = {}): InitialPetValues {
        // ENG-16914: `species` defaults to Dog, which is the long-standing behavior — only the cat arm
        // of the pets personalization experiment passes anything else. Note this value is load-bearing
        // beyond the UI: it drives the breed catalog (BreedUtils.formatBreedOptions), the Builder
        // quoteHasDogs/quoteHasCats attributes, and the same-named Statsig custom fields.
        const { currentSettings, isMultiPetLinked, defaultPlan, underwriter = "ptz-us", planCoverageOverride, species = Species.Dog } = params;

        const getCoverageSettings = (): CoverageSettings => {
            if (!!isMultiPetLinked && !!currentSettings) {
                return currentSettings;
            }
            if (!!planCoverageOverride) {
                return planCoverageOverride;
            }
            return DefaultPlanUtils.defaultPlanToCoverageSettings(defaultPlan, underwriter);
        };

        return {
            name: ``,
            species,
            gender: `F`,
            age: undefined,
            breedID: ``,
            coverageSettings: getCoverageSettings()
        };
    }

    static isValidPhoneNumber(phoneNumber: string): boolean {
        const isValid = phone(phoneNumber).isValid;
        return isValid;
    }

    static isValidQuote(quote: Quote | undefined): boolean {
        if (!quote) {
            return false;
        }
        const allPoliciesValid = (quote.policies ?? []).every(QuoteDataUtils.isValidPolicy);

        return !!quote.email && !!quote.ratingZipcode && allPoliciesValid;
    }

    static isValidPolicy(policy: Policy): boolean {
        const { name, species, breedID, gender, age, extra } = policy;
        return PartialPolicySchema.safeParse(policy).success && QuoteDataUtils.isValidPet({ name, species, breedID, gender, age, extra } as Pet);
    }

    static isValidPet(pet?: Pet): boolean {
        return PetSchema.safeParse(pet).success;
    }

    static isValidPurchase(quote: Quote | undefined, debug?: boolean) {
        if (debug) {
            try {
                return !!QuoteSchema.parse(quote);
            } catch (err) {
                if (err instanceof z.ZodError) {
                    console.log(err.issues);
                }
                return false;
            }
        }
        return QuoteSchema.safeParse(quote).success;
    }

    private static checkSpecies(species: "Dog" | "Cat", str?: string): string | undefined {
        if (!str) return;
        if (species === "Dog" && str.includes("_CAT")) {
            return str.replace(/_CAT/g, "_DOG");
        }
        if (species === "Cat" && str.includes("_DOG")) {
            return str.replace(/_DOG/g, "_CAT");
        }
        return str;
    }

    static quoteToPetQuote(quote: Quote): PetQuote {
        const petQuote: PetQuote = {
            underwriter: quote.underwriter,
            quoteId: quote.id,
            accountID: quote.accountID,
            firstName: quote.firstName,
            lastName: quote.lastName,
            email: quote.email,
            phone: quote.phone,
            discountCode: quote.discountCode ?? ``,
            affiliateCode: quote.affiliateCode ?? ``,
            billingInfo: quote.billingInfo,
            ratingAddress: quote.ratingAddress,
            marketing: quote.marketing,
            extra: quote.extra
        };

        // Persist the boolean explicitly, not just when truthy — a `false` (customer opted OUT of
        // paperless on the CA checkout step) must reach the stored quote so documents get mailed.
        if (quote.paperless !== undefined) {
            petQuote.paperless = quote.paperless;
        }

        // Mailing address (where physical documents are sent when not paperless). Only set when present
        // — US never populates it, so this is a no-op there.
        if (quote.mailingAddress !== undefined) {
            petQuote.mailingAddress = quote.mailingAddress;
        }

        if (!!quote.ratingZipcode && quote.ratingZipcode !== quote?.ratingAddress?.zipCode) {
            petQuote.ratingAddress = {
                zipCode: quote.ratingZipcode
            };
        } else if (!petQuote.ratingAddress && quote.ratingZipcode) {
            petQuote.ratingAddress = {
                zipCode: quote.ratingZipcode
            };
        }

        if (quote.transactionFee && quote.transactionFee.value !== undefined && isNumber(quote.transactionFee.value) && quote.transactionFee.symbol !== undefined) {
            petQuote.transactionFee = {
                value: quote.transactionFee.value,
                symbol: quote.transactionFee.symbol
            };
        }

        petQuote.extra = {
            ...petQuote.extra,
            ...quote.extra
        };

        if (quote.lastStepID !== undefined) {
            petQuote.extra.lastStepID = quote.lastStepID.toString();
            petQuote.extra.lastStepIDUpdatedAt = DateTime.utc().toISO();
        }

        if (quote.extra?.policyStepConsented) {
            petQuote.marketing = {};
            petQuote.marketing.hasEmailConsent = true;
            petQuote.marketing.hasSMSConsent = true;
        }

        if (quote.policies) {
            petQuote.pets = quote.policies.map(policy => {
                const petPolicy: PetPolicy = {
                    type: "pet-policy",
                    petID: policy?.id,
                    species: policy.species as any,
                    gender: policy.gender,
                    name: policy.name,
                    breedID: policy.breedID
                };

                if (policy.extra) {
                    petPolicy.extra = {
                        ...policy.extra
                    };
                }

                // If this is a temp pet ID, go ahead and remove it before we send it to the API
                if (!petPolicy?.petID || petPolicy.petID?.startsWith(TMP_POLICY_PREFIX)) {
                    delete petPolicy["petID"];
                }

                if (policy.birthMonth && policy.birthYear) {
                    petPolicy.birthDate = DateTime.utc(parseInt(policy.birthYear), parseInt(policy.birthMonth), 1).toISODate() ?? DateTime.utc().toISODate();
                } else if (typeof policy.age === "number") {
                    const age = policy.age === 0 ? 10 / 12 : policy.age;
                    petPolicy.birthDate = DateTime.utc().minus({ year: age }).toISODate() ?? DateTime.utc().toISODate();
                }

                const coverages = policy.coverageSettings?.coverages;
                const reimbursementRate = policy.coverageSettings?.amounts?.reimbursementRate;
                const annualDeductible = policy.coverageSettings?.amounts?.annualDeductible;
                const annualLimit = policy.coverageSettings?.amounts?.annualLimit;

                if (coverages) {
                    const baseCoverage: Coverage = {
                        type: [],
                        details: {},
                        extra: {}
                    };

                    // Fill in details for reimbursementRate, deductible, and annualLimit...

                    const accidentCoverage = coverages.find(c => c.type === "accident");
                    const illnessCoverage = coverages.find(c => c.type === "illness");

                    if ((accidentCoverage || illnessCoverage) && Array.isArray(baseCoverage.type)) {
                        if (accidentCoverage?.type === `accident`) {
                            baseCoverage.type.push("accident");
                            baseCoverage.id = this.checkSpecies(policy.species as Species, accidentCoverage?.id);
                            baseCoverage.extra = accidentCoverage.extra;
                        }
                        if (illnessCoverage?.type === `illness`) {
                            baseCoverage.type.push("illness");
                            // No need to update id and extra since they are the same as accidentCoverage
                        }
                        petPolicy.coverages = [baseCoverage];
                    }
                    if (reimbursementRate !== undefined) {
                        baseCoverage.details!.reimbursementPercent = {
                            value: reimbursementRate,
                            symbol: "%"
                        };
                    }

                    if (annualDeductible !== undefined) {
                        baseCoverage.details!.deductible = {
                            value: annualDeductible,
                            symbol: "$"
                        };
                    }

                    if (annualLimit !== undefined) {
                        baseCoverage.details!.annualLimit = {
                            value: annualLimit,
                            symbol: annualLimit === PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE ? Strings.UNLIMITED_ANNUAL_LIMIT_LABEL : "$"
                        };
                    }

                    petPolicy.coverages = [baseCoverage];

                    if (coverages.some(coverage => coverage.type === "wellness-gold")) {
                        const wellnessGoldCoverage = coverages.find(coverage => coverage.type === "wellness-gold");
                        petPolicy.coverages.push({
                            type: ["wellness"],
                            id: this.checkSpecies(policy.species as Species, wellnessGoldCoverage?.id),
                            extra: wellnessGoldCoverage?.extra
                        });
                    } else if (coverages.some(coverage => coverage.type === "wellness-platinum")) {
                        const wellnessPlatinumCoverage = coverages.find(coverage => coverage.type === "wellness-platinum");
                        petPolicy.coverages.push({
                            type: ["wellness"],
                            id: this.checkSpecies(policy.species as Species, wellnessPlatinumCoverage?.id),
                            extra: wellnessPlatinumCoverage?.extra
                        });
                    }
                }

                return petPolicy;
            });
        }

        return petQuote;
    }

    static getDefaultAmountsByUnderwriter(underwriter: PetUnderwriterType): DefaultPolicyAmounts | null {
        switch (underwriter) {
            case "ptz-us":
                return {
                    annualLimit: PublicConfig.PTZ_US.DEFAULT_ANNUAL_LIMIT,
                    annualDeductible: PublicConfig.PTZ_US.DEFAULT_ANNUAL_DEDUCTIBLE,
                    reimbursementRate: PublicConfig.PTZ_US.DEFAULT_REIMBURSMENT_RATE
                };
            case "ptz-ca":
                return {
                    annualLimit: PublicConfig.PTZ_CA.DEFAULT_ANNUAL_LIMIT
                };
            default:
                return null;
        }
    }

    static extractStartDateByCoverageType(type: CoverageType, coverageTypeDifferences?: CoverageTypeDifference[]): string | undefined {
        const typeDifferenceToCheck = coverageTypeDifferences?.find(coverageTypeDifference => coverageTypeDifference.type === type);
        return typeDifferenceToCheck?.startDate;
    }

    static extractCoveragesFromPetPolicy(pet: PetPolicy, underwriter?: PetUnderwriterType): CoverageSettings | undefined {
        const coverageSettings: CoverageSettings = {
            coverages: [],
            amounts: {},
            extra: {}
        };

        let defaultAmounts = null;

        if (underwriter) {
            defaultAmounts = QuoteDataUtils.getDefaultAmountsByUnderwriter(underwriter);
        }

        if (pet.coverages) {
            for (const coverage of pet.coverages) {
                const coverageNeedsDefaults = !coverage.id;

                if (coverage.details?.reimbursementPercent?.value) {
                    coverageSettings.amounts!.reimbursementRate = this.roundDollarAmount(coverage.details.reimbursementPercent.value);
                }

                if (coverage.details?.deductible?.value) {
                    coverageSettings.amounts!.annualDeductible = this.roundDollarAmount(coverage.details.deductible.value);
                }

                if (coverage.details?.annualLimit?.value) {
                    coverageSettings.amounts!.annualLimit = this.roundDollarAmount(coverage.details.annualLimit.value);
                }

                const hasAccidentCoverage = coverage.type?.includes("accident");
                if (hasAccidentCoverage || (!hasAccidentCoverage && coverageNeedsDefaults)) {
                    coverageSettings.coverages!.push({
                        type: "accident",
                        id: coverage?.id,
                        name: coverage?.name,
                        extra: coverage?.extra,
                        createDate: coverage?.createDate,
                        startDate: this.extractStartDateByCoverageType("accident", coverage?.coverageTypeDifferences)
                    });
                }

                const hasIllnessCoverage = coverage.type?.includes("illness");
                if (hasIllnessCoverage || (!hasIllnessCoverage && coverageNeedsDefaults)) {
                    coverageSettings.coverages!.push({
                        type: "illness",
                        id: coverage?.id,
                        name: coverage?.name,
                        extra: coverage?.extra,
                        createDate: coverage?.createDate,
                        startDate: this.extractStartDateByCoverageType("illness", coverage?.coverageTypeDifferences)
                    });
                }

                const hasPerilIllnessCoverage = coverage.type?.includes("peril");
                if (hasPerilIllnessCoverage) {
                    coverageSettings.coverages!.push({
                        type: "peril",
                        id: coverage?.id,
                        name: coverage?.name,
                        extra: coverage?.extra,
                        createDate: coverage?.createDate,
                        startDate: this.extractStartDateByCoverageType("peril", coverage?.coverageTypeDifferences)
                    });
                }

                if (coverage.type?.includes("wellness")) {
                    if (coverage.name === "Wellness Care Routine - WCR" || coverage.name === "Wellness Gold" || coverage.name === "Routine Wellness") {
                        coverageSettings.coverages!.push({
                            type: "wellness-gold",
                            id: coverage?.id,
                            name: coverage?.name,
                            extra: coverage?.extra,
                            createDate: coverage?.createDate,
                            startDate: this.extractStartDateByCoverageType("wellness", coverage?.coverageTypeDifferences),
                            preventivePrice: coverage.basePrice?.value
                        });
                    } else if (coverage.name === "Wellness Care Advanced - WCA" || coverage.name === "Wellness Platinum" || coverage.name === "Advanced Wellness") {
                        coverageSettings.coverages!.push({
                            type: "wellness-platinum",
                            id: coverage?.id,
                            name: coverage?.name,
                            extra: coverage?.extra,
                            createDate: coverage?.createDate,
                            startDate: this.extractStartDateByCoverageType("wellness", coverage?.coverageTypeDifferences),
                            preventivePrice: coverage.basePrice?.value
                        });
                    }
                }

                if (coverage.extra) {
                    coverageSettings.extra = {
                        ...coverageSettings.extra,
                        ...coverage.extra
                    };
                }
            }
            // Set default amounts only if no coverage amounts were found/set above
            if (!coverageSettings.amounts?.annualLimit && defaultAmounts?.annualLimit) {
                coverageSettings.amounts!.annualLimit = defaultAmounts.annualLimit;
            }

            if (!coverageSettings.amounts?.annualDeductible && defaultAmounts?.annualDeductible) {
                coverageSettings.amounts!.annualDeductible = defaultAmounts.annualDeductible;
            }

            if (!coverageSettings.amounts?.reimbursementRate && defaultAmounts?.reimbursementRate) {
                coverageSettings.amounts!.reimbursementRate = defaultAmounts.reimbursementRate;
            }
        }
        return coverageSettings;
    }

    static petQuoteToQuote(
        petQuote: PetQuote,
        underwriter?: PetUnderwriterType,
        options?: {
            includeBirthProps?: boolean;
            skipDefaults?: boolean;
            cleanUndefinedValues?: boolean;
            /** Normalizes the API's stored zipcode into the format the quote's consumers (step schemas,
             * form defaults) expect — e.g. CA passes a masker since the API strips the postal's space. */
            formatRatingZipcode?: (value: string) => string;
            /** Use the API's `netPrice` as the per-pet final price instead of deriving
             * base − discountAmount. Off by default — netPrice on the payload has no effect
             * until a form opts in. */
            showNetPrice?: boolean;
        }
    ): Quote {
        const { includeBirthProps = false, skipDefaults = false, cleanUndefinedValues = false, formatRatingZipcode, showNetPrice = false } = options || {};

        const quote: Quote = {
            underwriter: petQuote?.underwriter,
            id: petQuote.quoteId,
            accountID: petQuote?.accountID,
            firstName: petQuote?.firstName,
            lastName: petQuote?.lastName,
            email: petQuote.email,
            phone: petQuote.phone,
            ratingZipcode: formatRatingZipcode && petQuote.ratingAddress?.zipCode ? formatRatingZipcode(petQuote.ratingAddress.zipCode) : petQuote.ratingAddress?.zipCode,
            discountCode: skipDefaults ? petQuote?.discountCode : (petQuote?.discountCode ?? ``),
            affiliateCode: skipDefaults ? petQuote?.affiliateCode : (petQuote?.affiliateCode ?? ``),
            transactionFee: petQuote?.transactionFee,
            billingInfo: petQuote?.billingInfo,
            ratingAddress: petQuote?.ratingAddress,
            extra: petQuote?.extra,
            quoteStatus: petQuote?.quoteStatus,
            marketing: skipDefaults
                ? petQuote?.marketing
                : (petQuote?.marketing ?? {
                      hasEmailConsent: false,
                      hasSMSConsent: false
                  })
        };

        // Read the boolean explicitly so a stored `false` (opted out) survives the round-trip instead of
        // being dropped and defaulting back to paperless-on.
        if (petQuote.paperless !== undefined) {
            quote.paperless = petQuote.paperless;
        }

        if (petQuote.mailingAddress !== undefined) {
            quote.mailingAddress = petQuote.mailingAddress;
        }

        if (petQuote.pets && petQuote.pets.length > 0) {
            quote.policies = petQuote.pets.map(petPolicy => {
                const policy: Policy = {
                    id: petPolicy.petID,
                    accountId: petPolicy.accountId?.toString(),
                    name: petPolicy.name,
                    breedID: petPolicy.breedID,
                    breedName: petPolicy.breedName,
                    gender: petPolicy.gender as "M" | "F" | undefined,
                    species: petPolicy.species
                };

                if (petPolicy.extra) {
                    policy.extra = {
                        ...petPolicy.extra
                    };
                }

                if (petPolicy.birthDate) {
                    const birthDate = DateTime.fromISO(petPolicy.birthDate);
                    if (includeBirthProps) {
                        policy.birthMonth = birthDate.month.toString();
                        policy.birthYear = birthDate.year.toString();
                    }
                    policy.age = Math.floor(-birthDate.diff(DateTime.utc().endOf("day"), "years").years);
                }

                if (!petPolicy.coverages?.length) {
                    return policy;
                }

                // Use currency.js for price calculations
                let basePreTax = currency(0);
                let netPreTax = currency(0);
                let hasServerNetPrice = false;
                let taxAmount = currency(0);
                let discountAmount = currency(0);

                for (const coverage of petPolicy.coverages) {
                    if (coverage.basePrice?.value) {
                        basePreTax = basePreTax.add(coverage.basePrice.value);
                    }

                    // Server discounted price; coverages without one (e.g. wellness riders) count at base price.
                    if (coverage.netPrice?.value !== undefined) {
                        hasServerNetPrice = true;
                        netPreTax = netPreTax.add(coverage.netPrice.value);
                    } else if (coverage.basePrice?.value) {
                        netPreTax = netPreTax.add(coverage.basePrice.value);
                    }

                    if (coverage.taxAmount?.value) {
                        taxAmount = taxAmount.add(coverage.taxAmount.value);
                    }

                    if (coverage.discountAmount?.value) {
                        discountAmount = discountAmount.add(coverage.discountAmount.value);
                    }

                    if (coverage.discounts?.length) {
                        policy.discounts = coverage.discounts.map(discount => ({
                            id: discount.id,
                            name: discount.name,
                            adjustment: discount.adjustment,
                            amount: discount.amount
                        }));
                    }
                }

                const basePrice = basePreTax.add(taxAmount);
                let finalPrice = basePrice.subtract(discountAmount);

                // Trust the API's netPrice as the final price; discountAmount becomes base − net so
                // displays still reconcile (final + discount = base).
                const useServerNetPrice = showNetPrice && hasServerNetPrice;
                if (useServerNetPrice) {
                    finalPrice = netPreTax.add(taxAmount);
                    discountAmount = basePrice.subtract(finalPrice);
                }

                if (finalPrice.value > 0) {
                    policy.basePrice = {
                        value: basePrice.value,
                        symbol: "$"
                    };

                    policy.discountAmount = {
                        value: discountAmount.value,
                        symbol: "$"
                    };

                    policy.finalPrice = {
                        value: finalPrice.value,
                        symbol: "$"
                    };
                }

                policy.coverageSettings = QuoteDataUtils.extractCoveragesFromPetPolicy(petPolicy, underwriter);

                const initialMonthlyPriceValue =
                    quote?.billingInfo?.frequency === "yearly" ? this.roundDollarAmount(finalPrice.divide(12).value) : this.roundDollarAmount(finalPrice.value);

                // Check if any of the initial attributes have changed
                const hasAgeChanged = policy.extra?.initialPetAge !== undefined && policy.extra.initialPetAge !== policy.age;
                const hasBreedChanged = policy.extra?.initialPetBreed !== undefined && policy.extra.initialPetBreed !== policy.breedID;
                const hasSpeciesChanged = policy.extra?.initialPetSpecies !== undefined && policy.extra.initialPetSpecies !== policy.species;

                if (petPolicy?.extra?.initialMonthlyPrice === undefined || hasAgeChanged || hasBreedChanged || hasSpeciesChanged) {
                    // set initial pet values
                    policy.extra = {
                        ...policy.extra,
                        initialMonthlyPrice: {
                            value: initialMonthlyPriceValue,
                            symbol: "$"
                        },
                        initialPetAge: policy.age,
                        initialPetBreed: policy.breedID,
                        initialPetSpecies: policy.species
                    };
                }

                return policy;
            });
        }

        if (petQuote.extra?.lastStepID) {
            quote.lastStepID = petQuote.extra.lastStepID;
        }

        if (cleanUndefinedValues) {
            return Object.fromEntries(Object.entries(quote).filter(([_, value]) => value !== undefined)) as Quote;
        }

        return quote;
    }

    static roundDollarAmount(value: number | undefined): number {
        if (typeof value !== "number") return 0;
        return currency(value).value;
    }
    /**
     * Parses authenticated quote data from cookie and converts it to Quote format
     * @param cookieValue - The raw cookie value containing JSON-stringified PetQuote
     * @param underwriter - The underwriter type for the quote
     * @param includeBirthProps - Whether to include birth properties in the conversion
     * @returns {Quote | null} - Parsed and converted Quote data or null if invalid
     */
    static parseAuthenticatedQuoteData({
        cookieValue,
        underwriter,
        includeBirthProps
    }: {
        cookieValue: string | undefined;
        underwriter: PetUnderwriterType;
        includeBirthProps?: boolean;
    }): Quote | null {
        if (!cookieValue) {
            return null;
        }

        try {
            const authenticatedPetQuote: PetQuote = JSON.parse(cookieValue);

            // Convert PetQuote to Quote format
            const quote = QuoteDataUtils.petQuoteToQuote(authenticatedPetQuote, underwriter, { includeBirthProps, skipDefaults: true, cleanUndefinedValues: true });

            return quote;
        } catch (error) {
            console.error("❌ Failed to parse authenticated quote data:", error);
            return null;
        }
    }

    /**
     * Parses pre-auth quote values from cookie and validates with schema
     * @param cookieValue - The raw cookie value containing JSON-stringified Quote data
     * @returns {Quote | null} - Parsed and validated Quote data or null if invalid
     */
    static parsePreAuthQuoteValues({ cookieValue }: { cookieValue: string | undefined }): Quote | null {
        if (!cookieValue) {
            return null;
        }

        try {
            const rawQuoteData = JSON.parse(cookieValue) as Quote;

            if ("phone" in rawQuoteData && !rawQuoteData.phone) {
                rawQuoteData.phone = undefined;
            }

            // Validate with PartialQuoteSchema
            const validationResult = PartialQuoteSchema.safeParse(rawQuoteData);

            if (!validationResult.success) {
                console.error("❌ Invalid pre-auth quote data schema:", validationResult.error);
                return null;
            }

            return validationResult.data;
        } catch (error) {
            console.error("❌ Failed to parse pre-auth quote values:", error);
            return null;
        }
    }
}
